import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '@/api/client';
import { authApi } from '@/api/auth';
import type { User } from '@/types';

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;

  initialize: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName: string) => Promise<void>;
  logout: () => void;
  refreshAccessToken: () => Promise<void>;
  updateUser: (user: Partial<User>) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      isInitialized: false,

      initialize: async () => {
        const { accessToken, refreshToken } = get();

        if (accessToken) {
          api.setAccessToken(accessToken);
          try {
            const user = await authApi.me();
            set({ user, isAuthenticated: true, isInitialized: true });
          } catch {
            // Token might be expired, try refresh
            if (refreshToken) {
              try {
                await get().refreshAccessToken();
                const user = await authApi.me();
                set({ user, isAuthenticated: true, isInitialized: true });
              } catch {
                get().logout();
              }
            } else {
              get().logout();
            }
          }
        } else {
          set({ isInitialized: true });
        }
      },

      login: async (email, password) => {
        set({ isLoading: true });
        try {
          const response = await authApi.login({ email, password });
          api.setAccessToken(response.accessToken);
          set({
            user: response.user,
            accessToken: response.accessToken,
            refreshToken: response.refreshToken,
            isAuthenticated: true,
          });
        } finally {
          set({ isLoading: false });
        }
      },

      register: async (email, password, displayName) => {
        set({ isLoading: true });
        try {
          const response = await authApi.register({ email, password, displayName });
          api.setAccessToken(response.accessToken);
          set({
            user: response.user,
            accessToken: response.accessToken,
            refreshToken: response.refreshToken,
            isAuthenticated: true,
          });
        } finally {
          set({ isLoading: false });
        }
      },

      logout: () => {
        const { refreshToken } = get();
        if (refreshToken) {
          authApi.logout(refreshToken).catch(() => {});
        }
        api.setAccessToken(null);
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isInitialized: true,
        });
      },

      refreshAccessToken: async () => {
        const { refreshToken } = get();
        if (!refreshToken) throw new Error('No refresh token');

        const response = await authApi.refresh(refreshToken);
        api.setAccessToken(response.accessToken);
        set({
          accessToken: response.accessToken,
          refreshToken: response.refreshToken,
        });
      },

      updateUser: (updates) => {
        set((state) => ({
          user: state.user ? { ...state.user, ...updates } : null,
        }));
      },

      setTokens: (accessToken, refreshToken) => {
        api.setAccessToken(accessToken);
        set({ accessToken, refreshToken });
      },
    }),
    {
      name: 'spark-auth',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// Listen for unauthorized events
if (typeof window !== 'undefined') {
  window.addEventListener('auth:unauthorized', () => {
    const state = useAuthStore.getState();
    if (state.refreshToken) {
      state.refreshAccessToken().catch(() => {
        state.logout();
      });
    } else {
      state.logout();
    }
  });
}
